import { arrayRemoveWhere } from '@nzyme/utils/array/arrayRemoveWhere.js';
import { mapNotNull } from '@nzyme/utils/array/mapNotNull.js';
import { waitUntil } from '@nzyme/utils/waitUntil.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { computed, reactive, ref } from 'vue';
import { FileUploader } from './FileUploader.js';
/**
 * Composable for managing file uploads.
 * Shared across patient-app and doctor-app.
 */
export function useFilesUpload(options) {
    const fileUploader = useService(FileUploader);
    const files = ref([]);
    let uploadAbortController = null;
    // Show files with errors first
    const filesToShow = computed(() => {
        const filesList = files.value.slice();
        filesList.sort((a, b) => {
            if (a.error && !b.error) {
                return -1;
            }
            if (!a.error && b.error) {
                return 1;
            }
            return 0;
        });
        return filesList;
    });
    const filesAreReady = computed(() => files.value.every(isFileReady));
    const filesAreValid = computed(() => files.value.every(isFileValid));
    return reactive({
        files,
        filesToShow,
        filesAreValid,
        filesAreReady,
        getFileIds,
        getFilesForEvent,
        uploadFiles,
        deleteFile,
        clearFiles,
        submitFiles,
        waitUntilReady,
    });
    function getFileIds() {
        return mapNotNull(files.value, f => f.id);
    }
    /** Maps current files to the common File schema for chat events. */
    function getFilesForEvent() {
        return mapNotNull(files.value, f => {
            if (!f.id || f.error) {
                return null;
            }
            return {
                id: f.id,
                name: f.name,
                type: f.type,
                width: f.width,
                height: f.height,
            };
        });
    }
    async function uploadFiles(filesToUpload) {
        files.value.push(...filesToUpload);
        const target = options.uploadTarget;
        if (!uploadAbortController) {
            uploadAbortController = new AbortController();
        }
        const signal = uploadAbortController.signal;
        await Promise.all(filesToUpload.map(file => fileUploader.upload(file, {
            ...target,
            signal,
        })));
    }
    function deleteFile(file) {
        arrayRemoveWhere(files.value, f => f.tempId === file.tempId);
        // Clean up preview URL if it exists
        if (file.url) {
            URL.revokeObjectURL(file.url);
        }
    }
    function clearFiles() {
        // Abort any in-flight uploads before clearing state
        if (uploadAbortController) {
            uploadAbortController.abort();
            uploadAbortController = null;
        }
        if (options.cacheImagePreview) {
            // Image blob URLs were cached into the app's FileMetadataStore during
            // submitFiles() for immediate preview rendering. Only revoke non-image
            // URLs (e.g. PDFs) that were not cached.
            for (const file of files.value) {
                if (file.url && !file.type.startsWith('image/')) {
                    URL.revokeObjectURL(file.url);
                }
            }
        }
        else {
            for (const file of files.value) {
                if (file.url) {
                    URL.revokeObjectURL(file.url);
                }
            }
        }
        files.value = [];
    }
    /** Waits for all uploads to finish and returns file IDs and event data. */
    async function submitFiles() {
        if (!files.value.length) {
            return { valid: true, fileIds: [], files: [] };
        }
        if (!filesAreReady.value) {
            await waitUntilReady();
        }
        // Check validity after waiting for uploads to complete
        if (!filesAreValid.value) {
            return { valid: false, fileIds: [], files: [] };
        }
        if (options.cacheImagePreview) {
            for (const file of files.value) {
                if (file.id && file.url && file.type.startsWith('image/')) {
                    options.cacheImagePreview(file.id, file.url);
                }
            }
        }
        const fileIds = getFileIds();
        const filesForEvent = getFilesForEvent();
        return { valid: true, fileIds, files: filesForEvent };
    }
    function waitUntilReady() {
        return waitUntil(() => filesAreReady.value);
    }
    function isFileValid(file) {
        return !!file.id && !file.error;
    }
    function isFileReady(file) {
        return (
        // File has been uploaded
        (!!file.id && file.progress == null) ||
            // ... or it has an error
            !!file.error);
    }
}
//# sourceMappingURL=useFilesUpload.js.map